<template>
  <div class="content-edit-page">
    <section class="content-edit-page__header">
      <button type="button" class="content-edit-page__back" @click="goBack">
        <i class="bi bi-arrow-left"></i>
        <span>Назад к контенту</span>
      </button>
      <h1 class="content-edit-page__title">Редактирование контента</h1>
    </section>

    <div
      v-if="isLoading"
      class="content-edit-page__loading d-flex align-items-center justify-content-center"
    >
      <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
      Загрузка контента...
    </div>

    <div v-else-if="loadError" class="alert alert-danger">{{ loadError }}</div>

    <section v-else-if="contentData" class="content-edit-page__editor">
      <ContentModal
        mode="edit"
        :standalone="true"
        :contentData="contentData"
        @close="goBack"
      />
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ContentModal from './ContentModal.vue'

export default {
  name: 'ContentEditPage',
  components: {
    ContentModal,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()

    const isLoading = ref(false)
    const loadError = ref('')
    const contentData = ref(null)

    const streamId = computed(() => String(route.params.streamId || '').trim())
    const contentId = computed(() => String(route.params.contentId || '').trim())

    const loadContent = async () => {
      if (!contentId.value) {
        loadError.value = 'Не указан идентификатор контента.'
        contentData.value = null
        return
      }

      isLoading.value = true
      loadError.value = ''
      try {
        const { data } = await axios.get(`/api/contents/${encodeURIComponent(contentId.value)}`)
        contentData.value = data || {}
      } catch (error) {
        console.error('Ошибка при получении контента для редактирования:', error)
        loadError.value = 'Не удалось загрузить данные контента.'
        contentData.value = null
      } finally {
        isLoading.value = false
      }
    }

    const goBack = () => {
      if (!streamId.value) {
        router.back()
        return
      }
      router.push(`/streams/${encodeURIComponent(streamId.value)}/content`)
    }

    watch(contentId, () => {
      loadContent()
    })

    onMounted(async () => {
      await loadContent()
    })

    return {
      isLoading,
      loadError,
      contentData,
      goBack,
    }
  },
}
</script>

<style scoped>
.content-edit-page {
  min-height: 100%;
  padding: 8px 14px 12px;
}

.content-edit-page__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.content-edit-page__back {
  height: 34px;
  border: 1px solid #d2def6;
  border-radius: 8px;
  background: #fff;
  color: #2f4568;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.content-edit-page__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #273955;
}

.content-edit-page__loading {
  min-height: 160px;
  color: #5b6f95;
  font-size: 14px;
}

.content-edit-page__editor {
  min-height: calc(100vh - 190px);
}

.content-edit-page :deep(.content-page-layout) {
  padding: 0;
  min-height: 0;
}

.content-edit-page :deep(.modal-container.content-modal.content-modal--standalone) {
  width: 100%;
  height: auto;
  max-height: none;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  background: transparent;
  margin: 0;
}

.content-edit-page :deep(.content-modal-header--standalone) {
  display: none;
}

.content-edit-page :deep(.content-modal-body) {
  padding: 0;
}

.content-edit-page :deep(.content-modal-footer) {
  position: sticky;
  bottom: 0;
  z-index: 3;
}
</style>
